import Image from 'next/future/image';
import { useRouter } from 'next/router';
import Carousel from 'nuka-carousel';
import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { srcset } from '@/helper/srcset';

import SVModal from '../common/sv-modal';
import Buttons from './revamp/image-gallery/buttons';

const defaultControlsConfig = {
  pagingDotsStyle: {
    fill: 'white',
  },
};

const ImageSlider = ({ images, isMobile, promotions }: any) => {
  const [autoPlay, setAutoPlay] = useState(1);
  const { locale }: any = useRouter();

  const firstPromotionTitle =
    promotions.length > 0
      ? promotions[0][`Banner_${locale === 'fr' ? 'FR' : 'EN'}_Title`]
      : '50% Off 12 weeks'; // Default text if no promotions exist
  const firstPromotionBadge =
    promotions.length > 0
      ? promotions[0][`Banner_${locale === 'fr' ? 'FR' : 'EN'}_Badge`]
      : 'Flash Sale'; // Default text if no promotions exist
  const firstPromotionLink =
    promotions.length > 0
      ? promotions[0][`Banner_${locale === 'fr' ? 'FR' : 'EN'}_Link`]
      : '#'; // Default text if no promotions exist

  const scrollToUnits = () => {
    setTimeout(() => {
      const reviews = document.getElementById('sv-units-hours-tabs');
      if (!reviews) return;
      window.scrollTo({ top: reviews.offsetTop - 80, behavior: 'smooth' });
    }, 500);
  };

  const removeAttr = () => {
    const removeInertAttr = document.getElementsByClassName(
      'slide slide-current slide-visible'
    );
    for (let i = 0; i < removeInertAttr.length; i += 1) {
      removeInertAttr[i].removeAttribute('inert');
    }
  };

  useEffect(() => {
    removeAttr();

    const htmlElement = document.documentElement;
    const modal = document.getElementById('extraImagesModalMobile');

    if (modal) {
      modal.addEventListener('show.bs.modal', () => {
        htmlElement.classList.add('sv-imagesModal-scroll');
      });

      modal.addEventListener('hidden.bs.modal', () => {
        htmlElement.classList.remove('sv-imagesModal-scroll');
      });
    }

    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        // Handle first modal
        const closeButton1 = document.querySelector(
          '#extraImagesModalMobile [data-bs-dismiss="modal"]'
        ) as HTMLElement;
        if (closeButton1) {
          closeButton1.click();
          setAutoPlay(0);
        }

        // Handle second modal
        const closeButton2 = document.querySelector(
          '#playVideo [data-bs-dismiss="modal"]'
        ) as HTMLElement;
        if (closeButton2) {
          closeButton2.click();
        }
      }
    };

    document.addEventListener('keydown', handleEscKey);

    return () => {
      if (modal) {
        modal.removeEventListener('show.bs.modal', () => {
          htmlElement.classList.add('sv-imagesModal-scroll');
        });

        modal.removeEventListener('hidden.bs.modal', () => {
          htmlElement.classList.remove('sv-imagesModal-scroll');
        });
      }
      document.removeEventListener('keydown', handleEscKey);
    };
  }, []);

  return (
    <div id="carouselExampleFade" className="carousel slide carousel-fade">
      <Carousel
        defaultControlsConfig={defaultControlsConfig}
        renderCenterLeftControls={({ previousSlide }) => (
          <button
            className="carousel-control-prev"
            type="button"
            onClick={previousSlide}
            aria-label="Previous"
          >
            <span
              className="carousel-control-prev-icon"
              aria-hidden="true"
            ></span>
            <span className="visually-hidden">Previous</span>
          </button>
        )}
        renderCenterRightControls={({ nextSlide }) => (
          <button
            className="carousel-control-next"
            type="button"
            onClick={nextSlide}
            aria-label="Next"
          >
            <span
              className="carousel-control-next-icon"
              aria-hidden="true"
            ></span>
            <span className="visually-hidden">Next</span>
          </button>
        )}
      >
        {images?.slice(1, images?.length)?.map((image: any) => (
          <div key={image?.id} className="carousel-item active video-wrapper">
            <Image
              sizes="(max-width: 375px) 300px, (max-width: 576px) 200px, (max-width: 768px) 265px, (max-width: 992px) 200px, (max-width: 1280px) 275px"
              src={
                image?.Image?.data?.attributes?.formats?.small?.url ??
                image?.Image?.data?.attributes?.url
              }
              alt={image?.Image?.data?.attributes?.alternativeText}
              width="700"
              height="285"
              className="d-block w-100"
              data-bs-toggle="modal"
              data-bs-target="#extraImagesModalMobile"
              loading="lazy"
            />
          </div>
        ))}
      </Carousel>
      <Buttons
        id="extraImagesModalMobile"
        videoId="playVideo"
        isMobile={isMobile}
      />
      <SVModal id="extraImagesModalMobile" classNames="modal-fullscreen">
        <SVModal.Header>
          <button
            className="btn sv-play-video-btn"
            data-bs-toggle="modal"
            data-bs-target="#playVideo"
          >
            <span className="sv-btn-icon me-1">
              <img
                src="/images/sv-play-video-white-icon.svg"
                alt={locale === 'en' ? 'Play Video' : 'Voir vidéo'}
              />
            </span>
            <FormattedMessage id="playVideo" />
          </button>
        </SVModal.Header>
        <SVModal.Body classNames="p-0">
          {images &&
            images
              ?.slice(1, images?.length)
              .map((image: any, index: number) => (
                <img
                  style={{ marginBottom: '16px', width: '100%' }}
                  key={
                    image?.Image?.data?.attributes?.formats?.small?.url || index
                  }
                  {...srcset(
                    image?.Image?.data?.attributes?.formats?.small?.url ||
                      image?.Image?.data?.attributes?.url,
                    200,
                    null,
                    null
                  )}
                  alt={image?.Image?.data?.attributes?.alternativeText}
                  loading="lazy"
                />
              ))}
        </SVModal.Body>
        <SVModal.Footer classNames="d-flex align-items-center justify-content-between">
          <div>
            <a
              href={firstPromotionLink}
              className="sv-deal-pill text-decoration-none mb-2"
              id="dealPill"
            >
              {firstPromotionBadge}
            </a>
            <h6 className="sv-hour-day mb-0 mt-2">{firstPromotionTitle}</h6>
          </div>
          <button
            className="btn sv-play-video-btn"
            type="button"
            data-bs-dismiss="modal"
            onClick={() => scrollToUnits()}
          >
            <FormattedMessage id="seeAvailableUnits" />
          </button>
        </SVModal.Footer>
      </SVModal>
      <SVModal
        id="playVideo"
        classNames="modal-dialog-centered sv-playVideo-modal"
      >
        <SVModal.Header closeButton={false} classNames="border-0">
          <button
            className="btn sv-close-btn btn-close"
            type="button"
            data-bs-dismiss="modal"
            onClick={() => setAutoPlay(0)}
          >
            <img src="/images/sv-modal-cross-icon.svg" alt="Cross icon" />
          </button>
        </SVModal.Header>
        <SVModal.Body classNames="p-0">
          <iframe
            style={{
              borderRadius: '10px',
              transform: 'translateZ(0px)',
            }}
            src={`${images?.[0]?.Video_URL}?h=500&autoplay=${autoPlay}&muted=1&controls=1&loop=0`}
            width="100%"
            height="400"
            loading="lazy"
          />
        </SVModal.Body>
      </SVModal>
    </div>
  );
};

export default ImageSlider;
